import { Modal } from 'antd';
import { FormInstance } from 'antd/lib/form';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isDone, isPending } from '../../constants/loading_state';
import { usePrevious } from '../../hooks/use_previous';
import { useUser } from '../../hooks/use_user';
import { getSpecies } from '../../redux/reference_data';
import { getActivationLoadingState, requestWaterbodyActivation } from '../../redux/waterbody';
import { WaterbodyActivationRequestForm } from './waterbody_activation_request_form';

interface WaterbodyActivationRequestModalProps {
  open: boolean;
  onCancel: () => void;
  urlSlug: string;
}

const WaterbodyActivationRequestModal = (props: WaterbodyActivationRequestModalProps) => {
  const { urlSlug, onCancel, open } = props;
  const species = useSelector(getSpecies);
  const { user } = useUser();
  const loadingState = useSelector(getActivationLoadingState);
  const formRef = useRef<FormInstance>(null);
  const prevLoadingState = usePrevious(loadingState);
  const dispatch = useDispatch();

  useEffect(() => {
    const wasSubmitting = isPending(prevLoadingState);
    const submitSuccess = isDone(loadingState);
    if (wasSubmitting && submitSuccess) {
      handleCancel();
    }
  }, [loadingState, prevLoadingState]);

  const handleOk = () => {
    const form = formRef.current;
    interface Values {
      email: string;
      species: string[];
      clarity: number;
      description: string;
    }

    form
      .validateFields()
      .then((values: Values) => {
        if (user) {
          const formValues = { ...values };
          formValues.email = user.email;

          dispatch(
            requestWaterbodyActivation(
              urlSlug,
              formValues.email,
              formValues.species,
              formValues.clarity,
              formValues.description
            )
          );
        }
      })
      .catch((e) => null);
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <Modal
      title="Request Waterbody Activation"
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      destroyOnClose
      okText="Submit"
      okButtonProps={{
        loading: isPending(loadingState),
      }}
      cancelButtonProps={{
        disabled: isPending(loadingState),
      }}
    >
      <p>
        <strong>Thanks for letting us know that you're interested in this waterbody.</strong>
      </p>
      <p>
        Help us gather information about it by indicating which species you fish.
        {user == null
          ? ' Enter your email to be notified when it is activated.'
          : ` We will email you at ${user.email} when it is activated.`}
      </p>
      <WaterbodyActivationRequestForm species={species} formRef={formRef} user={user} />
    </Modal>
  );
};

export default WaterbodyActivationRequestModal;
